import Head from 'next/head'
import Hero from 'components/folds/common/hero-fold'
import { seedCache, fetchAllPostsOfType } from 'lib/wp-fetcher'
import { Container, Row, Col } from 'react-bootstrap'
import { useRouter } from 'next/router'
import LoadingPage from 'components/folds/common/loading'
import HtmlText from 'atoms/html-text'
import CtaFold from 'components/folds/common/cta-fold'
import LuckyWpTableOfContents from 'molecules/lwtoc'
import { fetchCtasForPost, fetchGeneralCtas } from 'lib/cta-fetcher'
import AuthorFold from 'components/folds/common/author-fold'
import Image from 'next/legacy/image'
import FAQ from 'components/folds/common/faq-fold'
import ReviewsFold from 'components/folds/common/reviews-fold'
import { fetchRecentReviews, fetchReviewsSummary } from 'lib/feedbackcompany-fetcher'
import { cachedFetch } from 'lib/cached-fetch'
import { fetchAuthor } from 'lib/author'
import { wikiPostTypeSlugMapping } from 'lib/wiki-cpts'
import { fetchPostIdsOfType } from '../../lib/wp-fetcher'
import Video from 'components/folds/common/single-video-fold'
import { RankMathData } from 'lib/rank-math'
import parse from 'html-react-parser'
import { compareTwoStrings } from 'string-similarity'
import Share from 'components/share';

export default function WikiPage({
  article,
  author,
  featuredMedia,
  tableOfContentsHtml,
  generalCtaPosts,
  ctaPosts,
  reviewData,
  rankMathHead
}) {

  let overruleIntro = false

  function checkIntroText(article) {
    const firstParagraph = article?.content?.rendered.split('</p>')[0]

    if((featuredMedia || article.mainVideo) && compareTwoStrings(firstParagraph.substring(0, article?.excerpt.rendered.length), article?.excerpt.rendered) >= 0.5) {
      overruleIntro = true

      return firstParagraph
    }

    return article?.excerpt.rendered
  }

  const router = useRouter();

  if(router.isFallback) {
    return <LoadingPage />
  }

  const renderIntro = (forceShow = false) => (<>
    {(forceShow || featuredMedia || article.mainVideo) && <aside className="d-flex flex-column justify-content-center mb-5">
      <HtmlText body={checkIntroText(article)} />
      {featuredMedia && !article.mainVideo && <Image
        src={featuredMedia.source_url}
        width={featuredMedia.media_details.width}
        height={featuredMedia.media_details.height}
        objectFit="contain"
      />}
      {article.mainVideo && <Video url={article.mainVideo.acf.url} />}
    </aside>}
  </>)

  return (
    <div>
      <Head>
      {article?.link ? (
          parse(rankMathHead)
        ) : (
          <>
            <title>Fysiotherapie4all - {article?.title?.rendered}</title>
            <meta name="description" content={article?.excerpt.rendered} />
          </>
        )}
        {article && <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": article.link,
            },
            "headline": article.title?.rendered,
            "description": article.excerpt?.rendered,
            "image": featuredMedia?.source_url,
            "author": {
              "@type": "Person",
              "name": author.name,
            },
            "publisher": {
              "@type": "Organization",
              "name": "Fysiotherapie4all",
              "logo": {
                "@type": "ImageObject",
                "url": "https://fysiotherapie4all.nl/img/logo.png"
              }
            },
            "datePublished": article.date,
            "dateModified": article.modified,
          })}}
        />}
      </Head>
      <main>
        <Hero
          title={article?.title?.rendered}
          minHeight={50}
          dynamicTitleFontSize
        />
        <Container>
          <Row>
            <Col className="col-12 order-1 col-lg-3 order-lg-1 mb-3">
              <div className="d-lg-none">{renderIntro(true)}</div>
              <aside className="sticky-container">
                <CtaFold ctaPosts={ctaPosts} asSideItem />
              </aside>
            </Col>
            <Col className={`col-12 order-3 order-lg-2 ${tableOfContentsHtml !== '' ? 'hide-toc col-lg-6' : 'col-lg-8 col-xl-6'}`}>
              <div className="d-none d-lg-block">{renderIntro()}</div>
              <article>
                <HtmlText body={article?.content?.rendered.split('</p>').filter((value, key) => {
                  return key == 0 && overruleIntro ? false : true
                }).join('') } />
              </article>

              {article.acf?.faq && <FAQ
                faqItems={article.acf.faq}
                background={false}
              />}

              {article.articleAuthor && (
                <div className="mt-5">
                  <AuthorFold author={article.articleAuthor} />
                </div>
              )}
            </Col>
            {tableOfContentsHtml !== '' && <Col className="col-12 order-2 col-lg-3 order-lg-3 mb-3">
              <aside className="sticky-container">
                <LuckyWpTableOfContents html={tableOfContentsHtml} />
              </aside>
            </Col>}
          </Row>
        </Container>
        <CtaFold ctaPosts={generalCtaPosts} fullWidth />
        <ReviewsFold
          background={false}
          noSkew
          reviews={reviewData.reviews}
          summary={reviewData.summary}
        />

        <Share 
          shareUrl={article.link} 
        />

      </main>

      <style global jsx>{`
        .hide-toc .lwptoc {
          display: none;
        }

        .sticky-container {
          position: -webkit-sticky; /* Safari */
          position: sticky;
          top: 110px;
        }

        @media screen and (min-width: 992px) and (max-width: 1199px) {
          .sticky-container {
            top: 150px;
          }
        }

        @media screen and (min-width: 1200px) {
          main .container{
            max-width: 1400px;
          }
        }
      `}</style>
    </div>
  )
}

export async function getStaticPaths() {
  // Let's seed the cache a bit first!
  await seedCache();

  const paths = [];

  for await (const type of wikiPostTypeSlugMapping()) {
    const posts = await fetchAllPostsOfType(type.apiSlug);
    posts
      .map(post => {
        return {
          params: {
            id: post.id.toString(),
            slug: post.slug,
            articleType: type.slug,
            post,
          },
        }
      })
      .forEach(post => paths.push(post))
  }

  return { paths, fallback: true }
}

export async function getStaticProps({ params }) {
  if (!params.articleType && !params.slug) {
    return {
      props: {}
    }
  }

  const postTypeForSlug = wikiPostTypeSlugMapping().find(type => type.slug === params.articleType)?.apiSlug;
  const posts = await cachedFetch(`${process.env.NEXT_PUBLIC_API_URL}/wp-json/wp/v2/${postTypeForSlug}/?slug=${params.slug}`)

  if (posts.length < 1) {
    return {
      notFound: true,
      revalidate: parseInt(process.env.REVALIDATION_TIME_SECONDS),
    }
  }

  const article = posts[0]

  let author = { name: 'Maikel Korbmacher' }
  let featuredMedia = null
  if (article) {
    const authorHref = article._links?.author?.[0]?.href
    if (authorHref) {
      author = await cachedFetch(authorHref)
    }

    if (article.featured_media !== 0) {
      const url = article._links?.['wp:featuredmedia']?.[0]?.href;
      if (url) {
        try {
          featuredMedia = await cachedFetch(url)
        } catch (error) {
          console.error(`Error fetching media: ${url} is restricted.`)
        }
      }
    }
  }

  let tableOfContentsHtml = ''
  try {
    const tocRes = await cachedFetch(`${process.env.NEXT_PUBLIC_API_URL}/wp-json/ffa/v1/render_lwtoc_shortcode/${article.id}`)
    tableOfContentsHtml = tocRes.html
  } catch (error) {
    console.error(`Could not fetch table of contents for post ID ${article.id} (${params.articleType})`)
  }

  let ctaPosts = []
  try {
    ctaPosts = (await fetchCtasForPost(article.id)).slice(0, 1)
  } catch (error) {
    console.error(`Could not fetch CTAs for post ID ${article.id} (${params.articleType})`)
  }

  if (article.acf.main_video) {
    article.mainVideo = (await fetchPostIdsOfType(article.acf.main_video, 'videos'))[0]
  }

  // Show author block only on this article types
  if(['chronic-pain', 'causes'].indexOf(postTypeForSlug) >= 0) {
    article.articleAuthor = await fetchAuthor(article);
  }

  return {
    props: { 
      article,
      author,
      featuredMedia,
      tableOfContentsHtml,
      generalCtaPosts: await fetchGeneralCtas(),
      ctaPosts,
      reviewData: {
        reviews: await fetchRecentReviews(),
        summary: await fetchReviewsSummary(),
      },
      rankMathHead: await RankMathData(article.link),
    },
    revalidate: parseInt(process.env.REVALIDATION_TIME_SECONDS),
  }
}
